import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getMediaUrl, log } from '../../utils/environment.js';
import { propTypes as validationPropTypes } from '../../utils/validation.js';
import { withErrorBoundary } from '../common/EnhancedErrorBoundary.jsx';

const BlogCard = ({ blog }) => {
  const isValidBlog = React.useMemo(() => {
    return blog && typeof blog === 'object';
  }, [blog]);

  const {
    id,
    slug,
    title,
    excerpt,
    publish_date,
    read_time,
    views,
    category,
    featured_image_url,
    featured_image,
    author,
    tags
  } = blog || {};

  const imageUrl = React.useMemo(() => {
    if (!isValidBlog) return '/default-blog.jpg';
    
    if (featured_image_url) {
      return getMediaUrl(featured_image_url);
    }
    
    if (featured_image) {
      if (typeof featured_image === 'string') {
        return getMediaUrl(featured_image);
      }
      if (featured_image.url) {
        return getMediaUrl(featured_image.url);
      }
      if (featured_image.image) {
        return getMediaUrl(featured_image.image);
      }
    }
    
    return '/default-blog.jpg';
  }, [isValidBlog, featured_image_url, featured_image]);

  const formattedDate = React.useMemo(() => {
    if (!isValidBlog || !publish_date) return 'No date';
    try {
      return new Date(publish_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      log.warn('Invalid date format:', publish_date);
      return 'Invalid date';
    }
  }, [isValidBlog, publish_date]);

  const categoryName = React.useMemo(() => {
    if (!isValidBlog || !category) return 'Uncategorized';
    if (typeof category === 'string') return category;
    if (category?.name) return category.name;
    return 'Uncategorized';
  }, [isValidBlog, category]);

  const categorySlug = React.useMemo(() => {
    if (!isValidBlog || !category) return '';
    if (typeof category === 'object' && category.slug) return category.slug;
    if (typeof category === 'string') return category.toLowerCase().replace(/\s+/g, '-');
    return '';
  }, [isValidBlog, category]);

  const safeExcerpt = React.useMemo(() => {
    if (!isValidBlog || !excerpt || typeof excerpt !== 'string') {
      return 'No excerpt available';
    }
    const cleanExcerpt = excerpt.replace(/<[^>]*>/g, '');
    return cleanExcerpt.length > 120 
      ? cleanExcerpt.substring(0, 120) + '...' 
      : cleanExcerpt;
  }, [isValidBlog, excerpt]);

  const safeSlug = React.useMemo(() => {
    if (!isValidBlog) return 'unknown';
    return slug || id?.toString() || 'unknown';
  }, [isValidBlog, slug, id]);

  const safeTitle = React.useMemo(() => {
    if (!isValidBlog) return 'Untitled Article';
    return title || 'Untitled Article';
  }, [isValidBlog, title]);

  const safeReadTime = React.useMemo(() => {
    if (!isValidBlog) return '5 min read';
    return read_time || '5 min read';
  }, [isValidBlog, read_time]);

  const safeViews = React.useMemo(() => {
    if (!isValidBlog) return 0;
    return views || 0;
  }, [isValidBlog, views]);

  const safeAuthor = React.useMemo(() => {
    if (!isValidBlog) return 'Unknown Author';
    return author || 'Unknown Author';
  }, [isValidBlog, author]);

  const safeTags = React.useMemo(() => {
    if (!isValidBlog || !tags) return [];
    if (Array.isArray(tags)) return tags.slice(0, 3);
    return [];
  }, [isValidBlog, tags]);

  const handleImageError = (e) => {
    log.warn(`Failed to load image: ${imageUrl}`);
    e.target.src = '/default-blog.jpg';
    e.target.alt = 'Default blog image';
    e.target.onerror = null;
  };

  if (!isValidBlog) {
    return (
      <div className="enhanced-blog-card error-state">
        <div className="enhanced-blog-card-image">
          <img 
            src="/default-blog.jpg" 
            alt="Default blog" 
            loading="lazy"
            onError={(e) => {
              e.target.src = '/default-image.jpg';
            }}
          />
        </div>
        <div className="enhanced-blog-card-content">
          <h2 className="enhanced-blog-card-title">Article Not Available</h2>
          <p className="enhanced-blog-card-excerpt">This article could not be loaded.</p>
        </div>
      </div>
    );
  }

  return (
    <Link to={`/blog/${safeSlug}`} className="enhanced-blog-card" aria-label={`Read ${safeTitle}`}>
      <div className="enhanced-blog-card-image-wrapper">
        <img 
          src={imageUrl} 
          alt={safeTitle} 
          loading="lazy"
          onError={handleImageError}
          className="enhanced-blog-card-image"
        />
        {categorySlug && (
          <span className="enhanced-blog-card-category-badge">
            {categoryName}
          </span>
        )}
      </div>
      
      <div className="enhanced-blog-card-content">
        <h2 className="enhanced-blog-card-title">{safeTitle}</h2>
        <p className="enhanced-blog-card-excerpt">{safeExcerpt}</p>
        
        {safeTags.length > 0 && (
          <div className="enhanced-blog-card-tags">
            {safeTags.map((tag, index) => (
              <span key={index} className="enhanced-blog-card-tag">
                {typeof tag === 'string' ? tag : tag.name}
              </span>
            ))}
          </div>
        )}
        
        <div className="enhanced-blog-card-footer">
          <div className="enhanced-blog-card-author">
            <div className="enhanced-blog-card-author-avatar">
              {safeAuthor.charAt(0).toUpperCase()}
            </div>
            <div className="enhanced-blog-card-meta">
              <span className="enhanced-blog-card-author-name">{safeAuthor}</span>
              <div className="enhanced-blog-card-stats">
                <span className="enhanced-blog-card-date">{formattedDate}</span>
                <span className="enhanced-blog-card-separator">•</span>
                <span className="enhanced-blog-card-read-time">{safeReadTime}</span>
                {safeViews > 0 && (
                  <>
                    <span className="enhanced-blog-card-separator">•</span>
                    <span className="enhanced-blog-card-views">
                      👁 {safeViews}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <span className="enhanced-blog-card-read-more">
            Read More →
          </span>
        </div>
      </div>
    </Link>
  );
};

BlogCard.propTypes = {
  blog: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string,
    title: PropTypes.string,
    excerpt: PropTypes.string,
    publish_date: PropTypes.string,
    read_time: PropTypes.string,
    views: PropTypes.number,
    author: PropTypes.string,
    tags: PropTypes.array,
    category: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
      })
    ]),
    featured_image_url: validationPropTypes.imageUrl,
    featured_image: PropTypes.oneOfType([
      validationPropTypes.imageUrl,
      PropTypes.shape({
        url: validationPropTypes.imageUrl,
        image: validationPropTypes.imageUrl,
      })
    ]),
  }),
};

BlogCard.defaultProps = {
  blog: null,
};

export default withErrorBoundary(BlogCard, { 
  componentName: 'BlogCard',
  errorMessage: 'Failed to render blog card'
});